import { useState } from "react";
import { ArrowUpRight, Mail, MessageCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/sections/PageHero";
import { submitContact } from "../lib/api";

const interests = [
  { value: "capability-building", label: "Capability Building" },
  { value: "talent-deployment", label: "Talent Deployment" },
  { value: "growth-consulting", label: "Growth Consulting" },
  { value: "saas-pilot", label: "Deployedd Digital (SaaS)" },
  { value: "hiring-from-bench", label: "Hire from the bench" },
  { value: "general", label: "Something else" },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phonenumber: "",   // ADD THIS
    company: "",
    interest: "general",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    try {
      setLoading(true);
      await submitContact(form);
      toast.success("We'll be in touch within one business day.");
      setDone(true);
      setForm({ name: "", email: "", company: "", interest: "general", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or write to contact@deployedd.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        kicker="/ Contact"
        title="Tell us the problem statement."
        accent="We'll curate the solution."
        breadcrumb={["Contact"]}
        lead="Whether you're an institution, an enterprise, or a founder — start with the problem and we'll route you to the right pod within Deployedd."
      />

      <section className="container-wide py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ Send us a brief</p>
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-xs uppercase tracking-[0.25em] text-gray-500 mb-2" htmlFor="c-name">Your name</label>
                  <input
                    id="c-name"
                    data-testid="contact-name"
                    value={form.name}
                    onChange={onChange("name")}
                    required
                    className="w-full border-b border-ink-strong bg-transparent py-3 text-lg outline-none focus:border-[#9F381A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.25em] text-gray-500 mb-2" htmlFor="c-email">Email</label>
                  <input
                    id="c-email"
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                    className="w-full border-b border-ink-strong bg-transparent py-3 text-lg outline-none focus:border-[#9F381A] transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-gray-500 mb-2" htmlFor="c-phonenumber">Phone Number</label>
                <input
                  id="c-phonenumber"
                  data-testid="contact-phonenumber"
                  value={form.phonenumber}
                  onChange={onChange("phonenumber")}
                  className="w-full border-b border-ink-strong bg-transparent py-3 text-lg outline-none focus:border-[#9F381A] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-gray-500 mb-2" htmlFor="c-company">Company / Institution</label>
                <input
                  id="c-company"
                  data-testid="contact-company"
                  value={form.company}
                  onChange={onChange("company")}
                  className="w-full border-b border-ink-strong bg-transparent py-3 text-lg outline-none focus:border-[#9F381A] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-gray-500 mb-3">What is this about?</label>
                <div className="flex flex-wrap gap-2">
                  {interests.map((i) => (
                    <button
                      type="button"
                      key={i.value}
                      data-testid={`contact-interest-${i.value}`}
                      onClick={() => setForm((f) => ({ ...f, interest: i.value }))}
                      className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                        form.interest === i.value
                          ? "bg-ink text-white border-ink"
                          : "border-ink-strong text-ink hover:bg-[#F3F3F1]"
                      }`}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-gray-500 mb-2" htmlFor="c-message">Tell us the problem</label>
                <textarea
                  id="c-message"
                  data-testid="contact-message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={onChange("message")}
                  className="w-full border-b border-ink-strong bg-transparent py-3 text-lg outline-none focus:border-[#9F381A] transition-colors resize-none"
                />
              </div>
              <div className="flex items-center justify-between gap-4 flex-wrap pt-4">
                <p className="text-xs text-gray-500">We respond within one business day.</p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9F381A] transition-colors disabled:opacity-50"
                >
                  {loading ? "Sending…" : "Send brief"} <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
              {done && (
                <p data-testid="contact-success" className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-[#9F381A]">
                  / Received. A pod lead will reach out within one business day.
                </p>
              )}
            </form>
          </div>

          {/* Side */}
          <div className="lg:col-span-4 lg:col-start-9 space-y-10">
            <div className="border border-ink p-8">
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">/ Direct lines</p>
              <ul className="space-y-5">
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-[#9F381A]" />
                  <a href="mailto:contact@deployedd.com" data-testid="contact-email-link" className="link-underline text-base">contact@deployedd.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <MessageCircle className="w-4 h-4 mt-1 text-[#9F381A]" />
                  <a href="https://wa.me/918122431120" data-testid="contact-whatsapp-link" target="_blank" rel="noopener noreferrer" className="link-underline text-base">+91 81224 31120 · WhatsApp</a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#9F381A]" />
                  <span className="text-base">Chennai · Coimbatore · Remote</span>
                </li>
              </ul>
            </div>

            <div className="bg-ink text-white p-8">
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E] mb-4">/ For institutions</p>
              <h3 className="font-heading text-2xl tracking-tight mb-4">Run a Deployedd cohort on your campus.</h3>
              <p className="text-sm text-white/70 mb-6">We co-design with academic leadership and embed industry briefs into your existing curriculum.</p>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em]">Current intake — Aug & Jan</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
